/**
 * HTTP boundary for the *Accesos* surface (Plan J, J3).
 *
 * Four objects and one trail: **ámbitos** (`scopes` — the cut of the
 * data a person reads), **grupos** (the thing that carries an ámbito
 * plus a content matrix), **personas** (who is in which group) and the
 * **auditoría** of every change to those three.
 *
 * WHAT THIS MODULE MIRRORS, AND WHAT IT REFUSES TO INVENT
 * ------------------------------------------------------
 * Every payload below is a field-for-field mirror of
 * `builder/schemas/scopes.py` or `builder/schemas/groups.py` and of
 * nothing else. Two consequences that are the whole point:
 *
 * 1. **`null` is UNKNOWN, never zero.** `row_count`, `project_rows`,
 *    `file_bytes`, `estimated_rows` are all nullable on the backend
 *    with the reason written into `get_scope_route`'s docstring: *"A
 *    scope with no build row for a project reports `row_count: null`
 *    and `state: 'never_built'`; `0` is a real and different answer
 *    that only a `no_mapping` build produces."* Nothing here coalesces
 *    them.
 * 2. **Rule values keep their JSON type.** `ScopeRule.values` is
 *    `unknown[]`, straight from `DimensionValueOut.value`. The
 *    predicate binds type-directed and the runbook measured the cost of
 *    getting it wrong in DuckDB 1.5.3: against a VARCHAR column a rule
 *    of `[1]` makes DuckDB cast the COLUMN, so `'1'`, `'01'` and
 *    `'1.0'` all match — *"una edición de JSON indistinguible a ojo
 *    entrega tres vendedores distintos"*. A `String(value)` anywhere
 *    between the values endpoint and `rules_json` is a bug, and
 *    `__tests__/rules-editor.test.ts` is what says so.
 *
 * WHY IT IS ITS OWN MODULE
 * ------------------------
 * The same two reasons `dimensions-api.ts` gives in its own header:
 * `builder-api.ts` is past 900 lines and carries an intentional null
 * byte (grep reports it binary and needs `-a`), and every call goes
 * through `api.get/post/put/delete`, the single point that injects the
 * JWT and prefixes `/{tenant}/` — a hand-rolled `fetch` earns a 404
 * `no-prefix` under Plan E.
 *
 * ONE ENDPOINT NAMED HERE STILL DOES NOT EXIST — AND IT IS NOT THE AUDIT
 * ---------------------------------------------------------------------
 * Verified against the tree, not assumed. This header used to say TWO,
 * and the audit was one of them; it is not any more.
 * `builder/api/routes/access_audit.py` declares `GET` on
 * `/api/builder/admin/access-audit` and `main.py` includes the router,
 * so `accessAuditApi` below is a client for a route that answers. What
 * that section warned about is recorded in the past tense at
 * `accessAuditApi` itself rather than deleted, because the argument it
 * made — do not ship a trail-reading screen ahead of the trail's
 * endpoint — is about the next such screen, not about this one.
 *
 * The one that remains: there is still no `GET
 * /api/builder/admin/people`. `peopleApi.list` is a COMPOSITION over the
 * two routers that do exist, and it is marked at its definition with
 * what that costs.
 *
 * J4 ADDED A SECOND FOR A SLICE, AND IT IS CLOSED
 * -----------------------------------------------
 * `aiCostApi.estimate` used to name a path the builder did not serve,
 * so both screens that read it — the group AI tab and the Accesos audit
 * page — rendered every project as "sin estimar" beside a 404, on the
 * one control Pablo asked for as a COST LEVER. It now names
 * `GET /api/builder/admin/groups/insight-cost/{slug}`, which is
 * mounted, guarded by `manage_users` like the screens that read it, and
 * a PROXY of the portal's own estimator rather than a second one. The
 * argument that route's docstring makes about the guard is the answer
 * to the question this header used to leave open.
 *
 * And one FIELD went the same way: `GroupOut` carries `ai_config_json`
 * now and `PATCH` writes it, so it is REQUIRED here. It was optional
 * while the server neither sent nor accepted it, because `undefined`
 * was the only honest reading — a switch cannot say "off" about a
 * document nobody sent — and that optionality is what made every
 * control on the AI tab inert.
 *
 * Both screens are MOUNTED now (`App.tsx`, behind `AdminRoute
 * flag="manage_users"`), so the old arrangement — write the client, ship
 * the screen unreachable — is spent. What replaces it is the thing that
 * made the arrangement work in the first place: every payload below is a
 * field-for-field mirror of a Python schema, and the tests that pin it
 * read the Python source. A composition is honest as long as it says it
 * is one; an invented shape is not.
 */

import { api } from "@/lib/api";
import { adminUsersApi, type UserAdmin } from "@/lib/admin-users-api";

// ── Closed vocabularies ──────────────────────────────────────────────
//
// Exported as runtime arrays and not only as types: a TypeScript union
// cannot be iterated, and the dictionary-completeness tests have to walk
// them. This is the only mechanical thing between a value the backend
// adds later and a raw identifier on the analyst's screen.

/**
 * `schemas/scopes.SCOPE_PROJECT_STATES` — what one (scope, project)
 * pair is, as one word.
 *
 * Two members are NOT `scope_builds.status` values and that is why the
 * set is enumerated rather than derived: `never_built` means there is no
 * row at all (not `failed`, and emphatically not `0 rows`), and
 * `not_applicable` can come either from a recorded build or from the
 * file-free pre-check answering right now.
 *
 * `no_mapping` is a SUCCESS. It publishes an empty file — an empty
 * dashboard, not a 503 — and the runbook says to filter on the status
 * and not on the absence of warnings.
 */
export const SCOPE_PROJECT_STATES = [
  "built",
  "no_mapping",
  "building",
  "failed",
  "not_applicable",
  "never_built",
] as const;
export type ScopeProjectState = (typeof SCOPE_PROJECT_STATES)[number];

/**
 * `scopes/build.DB_STATUSES` plus `STATUS_SKIPPED_CONCURRENT`.
 *
 * `skipped_concurrent` never reaches `scope_builds` — no row is claimed
 * when it happens — so it appears in a rebuild RESPONSE and never in a
 * build row. It is in this list because the same dictionary paints
 * both.
 */
export const SCOPE_BUILD_STATUSES = [
  "building",
  "built",
  "failed",
  "no_mapping",
  "not_applicable",
  "skipped_concurrent",
] as const;
export type ScopeBuildStatus = (typeof SCOPE_BUILD_STATUSES)[number];

/** `schemas/groups.GROUP_HEALTH`, worst-first — the order the backend
 *  reports in, kept so a screen can sort by it without a second table. */
export const GROUP_HEALTH = ["grants_nothing", "no_members", "ok"] as const;
export type GroupHealth = (typeof GROUP_HEALTH)[number];

/**
 * `service_access_audit._ACTION_TARGETS`, mirrored key for key.
 *
 * Closed on both sides. `__tests__/audit-actions.test.ts` reads the
 * Python literal and fails when the two disagree, the way
 * `tests/test_enrich_summary_contract.py` pins `ENRICH_ERROR_KINDS` —
 * that test exists because a mirror treated as safe-to-drift produced a
 * screen that stayed silent about the most likely operational event of
 * its slice.
 */
export const ACCESS_AUDIT_ACTIONS = [
  "dimension.create",
  "dimension.update",
  "dimension.delete",
  "scope.create",
  "scope.update",
  "scope.delete",
  "scope.rebuild",
  "group.create",
  "group.update",
  "group.delete",
  "group.permission_set",
  "group.member_add",
  "group.member_remove",
  "user.effective_scope_change",
  "role_default.update",
  "user.create",
  "user.role_change",
  "user.deactivate",
  "user.reactivate",
  "user.login_allowed_change",
  "user.delete",
  "user.password_reset",
  "user.builder_access",
  "view_as.scope_preview",
] as const;
export type AccessAuditAction = (typeof ACCESS_AUDIT_ACTIONS)[number];

/**
 * `service_access_audit.ACTOR_KINDS` — the four `ck_access_audit_actor_kind`
 * allows. The column names the SURFACE a change came through, not the
 * person's role: a PervasiveMind analyst editing in the builder records
 * `provider_admin` even when their row carries the admin role.
 */
export const ACCESS_AUDIT_ACTOR_KINDS = [
  "provider_admin",
  "tenant_admin",
  "platform",
  "system",
] as const;
export type AccessAuditActorKind = (typeof ACCESS_AUDIT_ACTOR_KINDS)[number];

/** `EFFECTIVE_SCOPE_ID_PREFIX` — the one place the frontend may test
 *  for a derived id. It is a sha256 prefix and names nothing to a human,
 *  so it is never a label. */
export const EFFECTIVE_SCOPE_ID_PREFIX = "eff_";

/** `tenancy.scope.SCOPE_ALL` — the reserved, unrestricted scope. It has
 *  no rules, no file and no rebuild. */
export const SCOPE_ALL = "all";

/**
 * `ck_scopes_id_shape`, copied EXACTLY and deliberately case-sensitive.
 *
 * The runbook is explicit that `us-amazon` and `us_amazon` are two
 * different scopes on purpose, because the id IS the filename, and the
 * decisions log records that `set_current_scope` refuses the wrong case
 * rather than folding it. So the create form validates and never
 * lowercases what the analyst typed.
 */
export const SCOPE_ID_RE = /^[a-z][a-z0-9_-]{1,40}$/;

// ── Scopes ───────────────────────────────────────────────────────────

/** One rule: a dimension key and the values it keeps. AND-ed with its
 *  siblings — there is no OR grammar, and J3 refuses the unions that
 *  would need one rather than merging value lists into a cross product. */
export interface ScopeRule {
  dimension_key: string;
  values: unknown[];
}

export interface ScopeUsage {
  group_ids: string[];
  /** `{id, name}` per group carrying this scope. */
  groups: { id: string; name: string }[];
  /** People whose `users.effective_scope_id` IS this id right now. */
  bound_user_count: number;
  /** People in one of those groups. NOT summed with the above: on a
   *  person in exactly one group they are the same person twice. */
  member_user_count: number;
}

export interface ScopeOut {
  id: string;
  name: string;
  description: string;
  /** `explicit` | `effective` | the reserved `all`. */
  scope_kind: string;
  /** Normalised by `validate_rules_shape` on the way out, so two saves
   *  meaning the same thing are byte-identical. */
  rules: ScopeRule[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  editable: boolean;
  /** The backend's own sentence. Rendered verbatim. */
  not_editable_reason: string | null;
}

export interface ScopeSummaryOut extends ScopeOut {
  /** Projects the rules can actually cut. `0` is the number the list
   *  exists to make loud: a scope applicable nowhere grants nothing and
   *  looks perfect in `SELECT * FROM scopes`. `null` = not computed. */
  applies_to_count: number | null;
  built_count: number | null;
  failed_count: number | null;
  never_built_count: number | null;
  last_build_at: string | null;
  usage: ScopeUsage;
}

export interface ScopeProjectStatusOut {
  project_slug: string;
  project_name: string;
  applicable: boolean;
  /** A `PREDICATE_ERROR_KINDS` member. `dimension_not_usable_for_scoping`
   *  is the trap the runbook calls "el que muerde". */
  not_applicable_kind: string | null;
  not_applicable_detail: string | null;
  state: ScopeProjectState | string;
  last_build_status: string | null;
  row_count: number | null;
  project_rows: number | null;
  /** The swap FILENAME the build read. Rendered `font-mono`. */
  data_version: string | null;
  started_at: string | null;
  built_at: string | null;
  error_kind: string | null;
  error_detail: string | null;
  /** A `building` row past the staleness threshold. Age is not death —
   *  nothing clears it automatically. */
  stuck: boolean;
  /** The literal CLI command that clears it. A sentence, not a button. */
  stuck_hint: string | null;
  file_bytes: number | null;
  /** Only with `with_estimate`. One DuckDB reader and one scan per
   *  project, so it is opt-in and never on the list. */
  estimated_rows: number | null;
  estimate_error: string | null;
}

export interface ScopeDetailOut extends ScopeOut {
  applies_to: ScopeProjectStatusOut[];
  usage: ScopeUsage;
}

export interface ScopeListOut {
  scopes: ScopeSummaryOut[];
  total: number;
}

export interface ScopeImpactProjectOut {
  project_slug: string;
  project_name: string;
  applicable_before: boolean;
  applicable_after: boolean;
  not_applicable_kind_after: string | null;
  rows_before: number | null;
  rows_after: number | null;
  rows_after_error: string | null;
}

/**
 * The dry run of an edit. Reads only.
 *
 * Its `affected_group_ids` is what `PUT` requires echoed back in
 * `acknowledge_affects` — a caller who has not fetched the consequence
 * cannot name it. The server deliberately does NOT say whether the edit
 * widens or narrows; the row counts are the honest surrogate.
 */
export interface ScopeImpactOut {
  scope_id: string;
  affected_group_ids: string[];
  usage: ScopeUsage;
  affected_users: { id: string; email: string }[];
  projects: ScopeImpactProjectOut[];
  rules_valid: boolean;
  rules_error: string | null;
  normalised_rules: ScopeRule[] | null;
  /** The backend's sentence about what actually happens on save. */
  effective: string;
}

export interface ScopeUpdateOut {
  scope: ScopeOut;
  impact: ScopeImpactOut;
  effective: string;
  rebuild_endpoint: string;
  rules_changed: boolean;
  files_removed: string[];
  snapshots_deleted: number;
  readers_fail_closed_until_rebuilt: boolean;
}

export interface ScopeBuildOut {
  id: string;
  scope_id: string;
  project_slug: string;
  status: string;
  row_count: number | null;
  data_version: string | null;
  error_kind: string | null;
  error_phase: string | null;
  error_detail: string | null;
  started_at: string;
  built_at: string | null;
  stuck: boolean;
  stuck_hint: string | null;
}

export interface ScopeBuildListOut {
  builds: ScopeBuildOut[];
  /** The REAL count, never the page length. */
  total: number;
  offset: number;
  limit: number;
}

export interface ScopeRebuildOut {
  scope_id: string;
  project_slug: string;
  status: string;
  /** `built` and `no_mapping` both published one. */
  published_file: boolean;
  row_count: number | null;
  data_version: string | null;
  file_name: string | null;
  file_bytes: number | null;
  elapsed_ms: number;
  build_id: string | null;
  error: Record<string, unknown> | null;
  disk: Record<string, unknown> | null;
  effective: string;
}

export interface DimensionKeyProject {
  project_slug: string;
  project_name: string;
  dimension_id: string;
  label: Record<string, string>;
  origin: string;
}

/**
 * One key a scope rule may name, tenant-wide.
 *
 * A scope is per TENANT while `dimensions` is per PROJECT
 * (`uq_dimension_project_key`), so the per-project list cannot answer
 * "which keys may I rule on".
 *
 * The catalogue is ALREADY filtered to keys declared usable for
 * SCOPING: `dimension_key_catalogue` skips any dimension whose
 * `usable_as` lacks `scope`, because a rule on one of those resolves
 * `dimension_not_usable_for_scoping` at build time — the runbook's *"el
 * que muerde"*, since the row looks complete in every SELECT. So this
 * client does not re-derive scopability, and an EMPTY catalogue is the
 * state the editor must name: no dimension in the tenant may be ruled
 * on yet.
 *
 * `projects` therefore lists exactly the projects where a rule on this
 * key would cut, each with the `dimension_id` J2's value picker needs.
 * `labels` is a LIST and not a merge: where two projects label one key
 * differently, both are reported — reported, never reconciled.
 */
export interface DimensionKeyOut {
  key: string;
  labels: Record<string, string>[];
  projects: DimensionKeyProject[];
}

export interface DimensionKeyListOut {
  keys: DimensionKeyOut[];
  total: number;
}

const SCOPES = "/api/builder/admin/scopes";

export const scopesApi = {
  list: () => api.get<ScopeListOut>(SCOPES),

  /** `scope_kind` is not accepted — the server writes `explicit`. */
  create: (payload: {
    id: string;
    name: string;
    description: string;
    rules: ScopeRule[];
  }) => api.post<ScopeOut>(SCOPES, payload),

  /** `with_estimate` opens one DuckDB reader and scans the PROJECT file
   *  per project. Opt-in on the detail view only. */
  get: (id: string, opts?: { withEstimate?: boolean }) =>
    api.get<ScopeDetailOut>(`${SCOPES}/${encodeURIComponent(id)}`, {
      query: { with_estimate: opts?.withEstimate },
    }),

  /** The dry run. A POST because it carries a body, not because it
   *  changes anything. `rules: null` asks what the scope affects AS IT
   *  STANDS — the form to use before a delete. */
  impact: (
    id: string,
    rules: ScopeRule[] | null,
    opts?: { withEstimate?: boolean },
  ) =>
    api.post<ScopeImpactOut>(
      `${SCOPES}/${encodeURIComponent(id)}/impact`,
      { rules },
      { query: { with_estimate: opts?.withEstimate } },
    ),

  /**
   * Edit. `expected_updated_at` is required and a mismatch is 409; when
   * the RULES change and a group carries this scope,
   * `acknowledge_affects` must echo that group's ids from `impact`.
   *
   * It starts NO build. The runbook's §8 opens with the same sentence
   * the response carries: *"Por sí sola: nada."*
   */
  update: (
    id: string,
    payload: {
      name?: string;
      description?: string;
      rules?: ScopeRule[];
      expected_updated_at: string;
      acknowledge_affects?: string[];
    },
  ) => api.put<ScopeUpdateOut>(`${SCOPES}/${encodeURIComponent(id)}`, payload),

  /** 409, naming them, while a group carries it or a person is bound to
   *  it — and there is no force flag. */
  remove: (id: string) =>
    api.delete<void>(`${SCOPES}/${encodeURIComponent(id)}`),

  /**
   * Cut this scope's file for ONE project, now. Synchronous, and there
   * is no "rebuild everywhere" — a request that fanned out would hold
   * the tenant write lock, serially, behind a proxy that times out
   * first.
   *
   * When the proxy DOES time out, this response is not the source of
   * truth: `builds` is.
   */
  rebuild: (id: string, projectSlug: string) =>
    api.post<ScopeRebuildOut>(`${SCOPES}/${encodeURIComponent(id)}/rebuild`, {
      project_slug: projectSlug,
    }),

  builds: (
    id: string,
    opts?: { projectSlug?: string; limit?: number; offset?: number },
  ) =>
    api.get<ScopeBuildListOut>(`${SCOPES}/${encodeURIComponent(id)}/builds`, {
      query: {
        project_slug: opts?.projectSlug,
        limit: opts?.limit,
        offset: opts?.offset,
      },
    }),

  /** The tenant-wide key catalogue the rules editor picks from. */
  dimensionKeys: () => api.get<DimensionKeyListOut>(`${SCOPES}/dimension-keys`),
};

// ── Groups ───────────────────────────────────────────────────────────

export interface GroupMemberOut {
  user_id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  is_deleted: boolean;
  login_allowed: boolean;
  /** The person's denormalised binding, read from `users`. */
  effective_scope_id: string;
  /** The scope ids of ALL their groups, not only this one. More than
   *  one is what makes the effective scope a union. */
  group_scope_ids: string[];
  added_at: string | null;
  added_by_email: string | null;
}

/** Mirrors `service_groups.MAX_MEMBERS_PAGE`. Asking for more is a 422. */
export const MEMBERS_PAGE = 500;

export interface MembersOut {
  group_id: string;
  members: GroupMemberOut[];
  total: number;
  limit: number;
  offset: number;
}

/** A membership write reports BOTH deltas: a caller that submitted a
 *  stale list sees the removals it did not intend HERE, rather than
 *  from the person who lost access. */
export interface MembersUpdateOut {
  group_id: string;
  members: GroupMemberOut[];
  added: string[];
  removed: string[];
  note: string;
}

export interface GroupReportPermissionOut {
  report_key: string;
  section: string;
  action: string;
  granted: boolean;
  created_at: string;
  updated_at: string;
}

export interface GroupProjectPermissionOut {
  project_slug: string;
  section: string;
  action: string;
  granted: boolean;
  created_at: string;
  updated_at: string;
}

export interface RoleDefaultOut {
  role: string;
  section: string;
  action: string;
  granted: boolean;
  updated_at: string;
}

export interface GroupPermissionsOut {
  group_id: string;
  reports: GroupReportPermissionOut[];
  projects: GroupProjectPermissionOut[];
  /** The layer BENEATH the group rows: what a cell with no row means. */
  role_defaults: RoleDefaultOut[];
  /** The reports this group actually OPENS — the `(dashboard, view,
   *  granted)` cells. Granting the other twelve without it grants
   *  nothing at all, which is what `grants_nothing` reports. */
  entitled_report_keys: string[];
}

// ── The two JSONB halves of a group (Plan J, J4) ─────────────────────
//
// `groups.ai_config_json` and `groups.experience_json` are both real
// columns of portal migration 053, and they are at two very different
// stages. This module says which is which instead of presenting them as
// one feature, because the difference decides what the screen may claim.
//
// **`experience_json` IS on the wire.** `service_groups.parse_experience`
// validates it on the way in, `group_detail` re-validates it on the way
// out ("a profile that no longer parses has to be a loud 500 on the
// screen that edits it"), and `GroupOut.experience_json` is a required
// field with `default_factory=GroupExperience`. So it is REQUIRED here.
//
// **`ai_config_json` IS on the wire too, as of this slice.**
// `service_groups.parse_ai_config` validates it on the way in,
// `read_stored_ai_config` re-validates it on the way out, and
// `GroupOut.ai_config_json` is a required field with
// `default_factory=GroupAiConfig`. So it is REQUIRED here.
//
// It was OPTIONAL for one slice, and the reason is worth keeping,
// because it is the difference this module exists to preserve: while
// the server refused the field with a 400 and did not send it back,
// `undefined` was the only honest reading of it, and `GroupAiTab`
// rendered UNKNOWN — every control inert behind one sentence naming the
// refusal. Typing it as required back then and letting `undefined` flow
// into a checkbox would have rendered `insights_enabled: false`, which
// tells an analyst this group's narrative is switched off when nobody
// has said so. Now an empty column parses to exactly that `false`
// server-side — which is what the portal's own readers already conclude
// from `{}` — so the field is a fact rather than an absence.

/**
 * `groups.ai_config_json` — and ONLY the keys something reads.
 *
 * Two, and the list is short because the evidence is short. Every other
 * key the plan names for this document (a per-group switch for the
 * project-wide chat context; a verbosity) has no reader, no writer and
 * no name anywhere in the tree, and inventing one here would ship a
 * control that saves a key nothing will ever look at — which is worse
 * than a control that errors, because it never tells anybody.
 *
 * * `insights_enabled` — `ai_snapshot_service.scopes_with_insights_enabled`
 *   checks it with `is True`, deliberately: *"a JSONB column can hold
 *   `"yes"`, `1` or `null`, and only one of those was written by the
 *   toggle"*. The gate is OFF until somebody turns it on, so a key that
 *   is ABSENT means off — that is the reader's own semantics, mirrored,
 *   not a default this module invented. (Distinct from the whole
 *   document being absent, which is unknown. Same word, opposite
 *   epistemics, and the tab treats them differently.)
 * * `context_note` — read by `configs/templates.resolve_group_context_note`
 *   (chat and per-recipient mail) and `resolve_scope_context_note`
 *   (the shared insights snapshot). It orients the model and grants
 *   nothing: what a tool may read is decided by the FILE, so a note
 *   saying "you may also look at Mexico" changes the manners and not
 *   the rows.
 */
/** `schemas/groups.MAX_CONTEXT_NOTE`.
 *
 *  Not a database limit — the column is JSONB — a PROMPT limit. The note
 *  is appended verbatim to every prompt every member of this group
 *  reaches, and for the insights snapshot the notes of every group
 *  carrying one scope are JOINED, so an unbounded field is an unbounded
 *  per-request token cost on the tenant's own OpenAI organisation. Spelt
 *  here so the box can REPORT the length instead of earning a 400 for
 *  it — and reported rather than clipped, because clipping a paste
 *  would ship half a sentence into a prompt. */
export const MAX_CONTEXT_NOTE = 4000;

export interface GroupAiConfig {
  /** Never optional. The server materialises it from `{}`, which is
   *  what `scopes_with_insights_enabled` already concludes from an
   *  empty column, so `false` here is the reader's own semantics rather
   *  than a default this module invented. */
  insights_enabled: boolean;
  /** Stored STRIPPED, because that is what the reader does with it
   *  (`templates._coerce_template_string`), so `""` means no note. */
  context_note: string;
}

/** `schemas/groups.MAX_TEMPLATE_TILES` — and the portal's
 *  `schemas/home_board.WIDGET_LIMIT`, which is the AUTHORITY. The
 *  builder spells it a second time because the two apps share no widget
 *  schema package; this is the third spelling and it exists so the
 *  screen can say "18 / 40" instead of discovering the cap in a 400. */
export const MAX_TEMPLATE_TILES = 40;

/** `schemas/groups.TEMPLATE_FORBIDDEN_KINDS`. A board image is an upload
 *  owned by ONE person (`user_home_uploads` + `_owned_upload_or_404`,
 *  which 404s for anybody else), so an image tile provisioned into
 *  everybody else's board is a broken tile for every one of them. */
export const TEMPLATE_FORBIDDEN_KINDS = ["image"] as const;

/** `GroupMailTemplate.template` — the mail's voice. */
export const MAIL_TEMPLATES = ["exec", "ger", "oper", "anal", "zero"] as const;
export type MailTemplateStyle = (typeof MAIL_TEMPLATES)[number];

/** `GroupMailTemplate.length` — short · medium · long. The same three
 *  words a person picks on their OWN schedule in the portal, which is
 *  where the product's only length control actually is; the AI tab says
 *  so rather than inventing a second one per group. */
export const MAIL_LENGTHS = ["short", "medium", "long"] as const;
export type MailLength = (typeof MAIL_LENGTHS)[number];

/** `GroupMailTemplate.source_kind` — mirrors the DB CHECK
 *  `ck_user_schedules_source_shape`: `report` needs a `report_key`,
 *  `board` may not have one. */
export const MAIL_SOURCE_KINDS = ["report", "board"] as const;
export type MailSourceKind = (typeof MAIL_SOURCE_KINDS)[number];

export const MAIL_LOCALES = ["en", "es"] as const;
export type MailLocale = (typeof MAIL_LOCALES)[number];

/**
 * `schemas/groups.GroupBoardTemplate` — the Home board a member of this
 * group is provisioned with.
 *
 * `widgets` is `Record<string, unknown>[]` and NOT a widget union, which
 * is the same refusal the Python makes and for the same reason: the
 * portal's `schemas/home_board.py` is the single authority for what a
 * widget IS — eight kinds, each with its own fields, bounds and snapshot
 * selectors — and a second copy of it in the builder would drift the
 * first time somebody adds a field on that side. The portal re-validates
 * every template tile through `WidgetIn` when it provisions a member's
 * board and fails loud naming the group, the tile and pydantic's reason.
 */
export interface GroupBoardTemplate {
  widgets: Record<string, unknown>[];
  /** Re-sync replaces the GROUP's tiles on every board read and never
   *  touches a tile the person added themselves — provenance is the
   *  widget id, derived by `home_board_service`, never stored. Off makes
   *  the template a one-time provisioning and nothing else. */
  resync: boolean;
}

/**
 * `schemas/groups.GroupMailTemplate` — the default e-mail spec §8.2
 * gives a group, as it is STORED.
 *
 * **Nothing sends it and no screen edits it.** `experience_json` has one
 * reader in the tree and it looks at `board`; writing a member's
 * schedule row from this needs `user_report_schedules.origin`, which
 * does not exist (J4.6). The type is here because the document is on the
 * wire — `GroupExperience.mail` comes back on every group detail, and
 * `GroupExperienceTab` copies it into the next PATCH so that editing a
 * board tile does not delete a hand-written template. It is deliberately
 * NOT a form: a schedule form for a template nobody delivers tells an
 * administrator they scheduled something.
 *
 * **`recipients` is absent, and that absence is the safety property.** A
 * group default is rendered once per member, under that member's own
 * scope, and addressed to that member — so there is no way to express
 * "mail this group's report to somebody else", which is exactly the
 * shape that would ship one person's rows to an address with narrower
 * access. `trigger_kind` is absent too: a group default is a cadence, so
 * the rows written from it are `cron`; the data-driven and threshold
 * triggers carry per-row state that only means something for a schedule
 * one person authored and can edit.
 */
export interface GroupMailTemplate {
  source_kind: MailSourceKind;
  report_key: string | null;
  filters: Record<string, string>;
  include_ai: boolean;
  include_image: boolean;
  template: MailTemplateStyle;
  length: MailLength;
  goals: string[];
  custom_prompt: string | null;
  locale: MailLocale;
  cron: string;
  timezone: string;
  /** Whether members are provisioned with this mail switched ON. It is
   *  the GROUP's switch; a member's own pause lives on their row. */
  enabled: boolean;
}

/**
 * `groups.experience_json`, parsed and normalised by the server.
 *
 * Both halves optional and independent — `parse_experience` drops an
 * absent half rather than storing a JSON null — so `{}` is a group that
 * hands its members no defaults at all, and is also how a profile is
 * CLEARED. NEITHER HALF GRANTS ANYTHING: a tile provisioned onto a
 * member's board is still resolved against that member's permissions and
 * scope on every read and comes back `access="denied"` when they cannot
 * read it.
 *
 * Storage drops an absent half, but `GroupOut` serialises it as `null`
 * (pydantic default, no `exclude_none`), so a reader must treat `null`
 * exactly like an omitted key.
 */
export interface GroupExperience {
  board?: GroupBoardTemplate | null;
  mail?: GroupMailTemplate | null;
}

export interface GroupSummaryOut {
  id: string;
  name: string;
  description: string;
  scope_id: string;
  scope_name: string;
  scope_kind: string;
  member_count: number;
  entitled_report_count: number;
  project_permission_count: number;
  /** Portal migration 077: THE base group «Todo el contenido». */
  is_base: boolean;
  /** Opens every published report with fixed actions (D-G1 a, D-G2). */
  covers_all_reports: boolean;
  health: GroupHealth | string;
  health_detail: string | null;
  created_at: string;
  updated_at: string;
}

export interface GroupListOut {
  groups: GroupSummaryOut[];
  total: number;
}

export interface GroupOut extends GroupSummaryOut {
  scope: ScopeOut;
  /** NOT paginated here, on purpose: an access decision read off a
   *  silently shortened list is the worst output this feature has. */
  members: GroupMemberOut[];
  permissions: GroupPermissionsOut;
  /** Named after the COLUMN, like the field the PATCH accepts, so the
   *  Experiencia tab reads and writes one key instead of translating
   *  between two names for one document. On the DETAIL response only —
   *  a board template is a page of JSON and a list of thirty groups is
   *  not the place to ship thirty of them. */
  experience_json: GroupExperience;
  /** REQUIRED, and the change from optional is what made the AI tab
   *  real: the server now sends this document on every detail read, so
   *  the switch renders a fact instead of an absence. Detail response
   *  only, same as its sibling. */
  ai_config_json: GroupAiConfig;
}

export interface GroupUpdateOut {
  group: GroupOut;
  members: GroupMemberOut[];
  scope_changed: boolean;
  /** The backend's sentence about when the change takes effect. */
  note: string;
}

export interface GroupEffectiveOut {
  group_id: string;
  scope: ScopeOut;
  projects: ScopeProjectStatusOut[];
  members: GroupMemberOut[];
  /** Where the states that COST somebody access are said in words. */
  warnings: string[];
}

export interface GroupReportPermissionIn {
  report_key: string;
  section: string;
  action: string;
  granted: boolean;
}

export interface GroupReportPermissionKey {
  report_key: string;
  section: string;
  action: string;
}

export interface GroupProjectPermissionIn {
  project_slug: string;
  section: string;
  action: string;
  granted: boolean;
}

export interface GroupProjectPermissionKey {
  project_slug: string;
  section: string;
  action: string;
}

const GROUPS = "/api/builder/admin/groups";
const group = (id: string): string => `${GROUPS}/${encodeURIComponent(id)}`;

export const groupsApi = {
  list: () => api.get<GroupListOut>(GROUPS),

  /** `scope_id` is required and has no default: a group's purpose is to
   *  carry a restriction, so an omitted scope must raise rather than
   *  quietly produce one whose members inherit the widest scope. */
  create: (payload: {
    name: string;
    description: string;
    scope_id: string;
  }) => api.post<GroupOut>(GROUPS, payload),

  get: (id: string) => api.get<GroupOut>(group(id)),

  /**
   * Every field optional except the optimistic lock, and ABSENT MEANS
   * UNTOUCHED — the rule `service_groups` follows for all of them.
   *
   * The two JSONB documents are whole-document writes on the server
   * (*"a profile is written WHOLE — it is one document, like the board
   * it describes — so there is no half-edit to merge and no way for two
   * screens to interleave a template"*), so a caller sending
   * `experience_json` must send the ENTIRE profile, not the half it
   * edited. `GroupExperienceTab` keeps the loaded document in state and
   * merges its form into it for exactly that reason; sending only the
   * half on screen would clear the other one.
   *
   * `ai_config_json` follows the same whole-document rule, and the
   * consequence is the one to remember when reading `GroupAiTab`: a
   * PATCH naming only `insights_enabled` CLEARS `context_note`. The tab
   * therefore sends both, always. What it buys is that a stale tab
   * cannot put an old note back beside a lever somebody else just
   * flipped — it loses the optimistic-lock race instead, with a 409
   * naming both timestamps.
   */
  update: (
    id: string,
    payload: {
      name?: string;
      description?: string;
      scope_id?: string;
      ai_config_json?: GroupAiConfig;
      experience_json?: GroupExperience;
      expected_updated_at: string;
    },
  ) => api.patch<GroupUpdateOut>(group(id), payload),

  remove: (id: string) => api.delete<void>(group(id)),

  members: (id: string, opts?: { limit?: number; offset?: number }) =>
    api.get<MembersOut>(`${group(id)}/members`, {
      query: { limit: opts?.limit, offset: opts?.offset },
    }),

  /** EVERY member of a group, paged at the server's own cap.
   *
   *  The route refuses `limit > MAX_MEMBERS_PAGE` (500, `builder/
   *  service_groups.py`) with a 422 — and the first day in staging the
   *  two screens below asked for 2000, so every group came back
   *  unreadable and every person showed «sin grupo / desconocido». The
   *  cap is the server's to set; the client's job is to walk it. Pages
   *  are requested until `total` is reached, and a page that comes back
   *  empty before that raises instead of returning a short list that
   *  reads as "this person is in no group". */
  allMembers: async (id: string): Promise<GroupMemberOut[]> => {
    const out: GroupMemberOut[] = [];
    let offset = 0;
    for (;;) {
      const page = await groupsApi.members(id, { limit: MEMBERS_PAGE, offset });
      out.push(...page.members);
      if (out.length >= page.total) return out;
      if (page.members.length === 0) {
        throw new Error(
          `group ${id}: the server reports ${page.total} member(s) but ` +
            `stopped returning them after ${out.length}`,
        );
      }
      offset += page.members.length;
    }
  },

  replaceMembers: (id: string, userIds: string[]) =>
    api.put<MembersUpdateOut>(`${group(id)}/members`, { user_ids: userIds }),

  addMember: (id: string, userId: string) =>
    api.post<MembersUpdateOut>(
      `${group(id)}/members/${encodeURIComponent(userId)}`,
    ),

  /** 200 with the body, not 204 — a revocation answered with a bodyless
   *  "done" is how an admin never learns the person's binding and their
   *  groups now disagree. */
  removeMember: (id: string, userId: string) =>
    api.delete<MembersUpdateOut>(
      `${group(id)}/members/${encodeURIComponent(userId)}`,
    ),

  permissions: (id: string) =>
    api.get<GroupPermissionsOut>(`${group(id)}/permissions`),

  setReportPermissions: (id: string, cells: GroupReportPermissionIn[]) =>
    api.put<GroupPermissionsOut>(`${group(id)}/permissions/reports`, cells),

  /**
   * Delete cells. **Not the same as setting them to `false`**: with no
   * row the cell resolves through the person's OTHER groups and then
   * through the role default, whereas an explicit `false` survives
   * both. Conflating them widens access while believing it narrowed it,
   * and the returned matrix is the only thing that tells them apart.
   */
  deleteReportPermissions: (id: string, keys: GroupReportPermissionKey[]) =>
    api.delete<GroupPermissionsOut>(
      `${group(id)}/permissions/reports`,
      keys,
    ),

  setProjectPermissions: (id: string, cells: GroupProjectPermissionIn[]) =>
    api.put<GroupPermissionsOut>(`${group(id)}/permissions/projects`, cells),

  deleteProjectPermissions: (id: string, keys: GroupProjectPermissionKey[]) =>
    api.delete<GroupPermissionsOut>(
      `${group(id)}/permissions/projects`,
      keys,
    ),

  effective: (id: string) =>
    api.get<GroupEffectiveOut>(`${group(id)}/effective`),
};

// ── What per-scope insights cost (Plan J, J4.8) ──────────────────────

/**
 * `admin_ai_snapshots.GenerationEstimateOut.basis`, as a closed set.
 *
 * ONE member, and it earns the machinery. The field exists precisely so
 * *"no screen can render it as a measured figure"*, so the word is
 * painted through a dictionary like every other backend vocabulary in
 * this surface: the day the server answers something else, the screen
 * says so in a warning chip instead of printing a number that has
 * quietly changed meaning. That is also what keeps «estimación» BESIDE
 * the figure rather than in a footnote nobody reads.
 */
export const ESTIMATE_BASES = ["estimate"] as const;
export type EstimateBasis = (typeof ESTIMATE_BASES)[number];

/**
 * `hp_portal.api.routes.admin_ai_snapshots.GenerationEstimateOut`,
 * field for field.
 *
 * `generations_per_month` is arithmetic — `reports_with_insights ×
 * scopes × runs_per_month` — and `measured_last_30_days` is the only
 * measured number in the payload, a plain COUNT of rows actually
 * written. They travel together on purpose, *"so the two can be
 * compared instead of confused"*, and this screen renders both for the
 * same reason.
 */
export interface GenerationEstimateOut {
  project_slug: string;
  reports_with_insights: number;
  scopes: number;
  /** The scopes that earn a snapshot today: the unrestricted one plus
   *  every BUILT scope whose group has insights enabled. */
  scope_ids: string[];
  runs_per_month: number;
  generations_per_month: number;
  measured_last_30_days: number;
  basis: EstimateBasis | string;
}

/**
 * The builder's own route, and it is a PROXY.
 *
 * This block used to open "THE HANDLER FOR THIS PATH DOES NOT EXIST IN
 * THE BUILDER YET", in capitals, and the two screens that read it drew
 * every project as "sin estimar" next to a 404 — on the one control
 * Pablo asked for as a cost lever. The handler exists now:
 * `builder/api/routes/groups.py`, `insight_cost_route`.
 *
 * WHY IT IS ON THE GROUPS ROUTER AND NOT BESIDE THE OTHER SNAPSHOT
 * ENDPOINTS. That router (`builder/api/routes/admin_ai_snapshots.py`)
 * is guarded by `manage_ai`, and the two callers of this client — the
 * group's AI tab and the Accesos audit page — are `manage_users`
 * screens. An analyst who may flip the switch would have read a 403
 * where the cost of flipping it belongs. The guard follows the reader,
 * not the topic. (This paragraph replaced the note that raised the
 * question and left it open.)
 *
 * WHY IT PROXIES INSTEAD OF PORTING. The arithmetic starts from
 * `report_engine.get_reports()`, the registry of published reports that
 * only the portal's runtime holds, and continues through
 * `insight_scopes_by_project`, which reads the warmup's own answer to
 * "which scopes have a usable file". A port would be a second opinion
 * about which reports narrate and which scopes are built, and the
 * figure read before spending money has to be the figure the watcher
 * will actually spend. The builder forwards the portal's answer, and
 * its errors, verbatim — so a 404 arrives as a 404 with the portal's
 * sentence in it and never as an empty table or a zero.
 *
 * `runs_per_month` is REQUIRED and has no default, and that is the
 * portal's decision, mirrored by both the builder route and this
 * client: insights regenerate on a data swap, not on a calendar, so the
 * cadence is a fact about this client's loader schedule that only the
 * operator knows. *"A default here would turn a guess into a figure on
 * a screen."* Both screens therefore ASK, and show no number until
 * somebody answers.
 */
export const aiCostApi = {
  estimate: (
    projectSlug: string,
    opts: { runsPerMonth: number; scopes?: number },
  ) =>
    api.get<GenerationEstimateOut>(
      `/api/builder/admin/groups/insight-cost/${encodeURIComponent(projectSlug)}`,
      { query: { runs_per_month: opts.runsPerMonth, scopes: opts.scopes } },
    ),
};

// ── People ───────────────────────────────────────────────────────────

/**
 * One row of the Personas list: the `users` row plus what the group
 * router knows about them.
 *
 * `effective_scope_id` is `null` when NO GROUP reports it, which today
 * means the person is in no group. That is an unknown and it is
 * rendered as one: `users.effective_scope_id` is not carried by
 * `UserAdminOut`, so the only honest thing the screen can say is that
 * nobody computed a scope for this person — never a cheerful "Todo".
 */
export interface PersonRow {
  user: UserAdmin;
  /** `{id, name, scope_id, scope_name}` per group they belong to. */
  groups: {
    id: string;
    name: string;
    scope_id: string;
    scope_name: string;
  }[];
  /** The denormalised binding as any of their groups reports it, or
   *  `null` when no group does. */
  effective_scope_id: string | null;
  /** The scope ids their groups carry — the inputs of the union. */
  group_scope_ids: string[];
}

export interface PeopleListOut {
  people: PersonRow[];
  total: number;
  limit: number;
  offset: number;
  /** Groups whose membership could not be read, with the reason. The
   *  screen says so instead of reporting a smaller group count. */
  unreadableGroups: { group_id: string; group_name: string; error: string }[];
  /** When the portal's login record starts, so a person without logins
   *  reads "no logins since <date>"; `null` = nobody logged in yet. */
  loginsRecordStartsAt: string | null;
}

/**
 * The Personas list, ASSEMBLED IN THE BROWSER.
 *
 * **There is no `GET /api/builder/admin/people`.** Verified against the
 * tree: `builder/main.py` mounts `admin_users`, `admin_permissions`,
 * `admin_scopes` and `groups`, and none of them answers "which groups
 * is this person in". So this function does the join a `/people`
 * endpoint would do, and its cost is stated rather than hidden: one
 * call for the page of users, one for the group list, and one per group
 * for that group's members — bounded by the number of GROUPS, not by
 * the number of people.
 *
 * That is acceptable at the measured size (HP: 12 users) and it is the
 * wrong home for the join. It belongs on the server, where the
 * membership is one indexed read and where the person's real
 * `users.effective_scope_id` is in scope for everybody — including the
 * people this composition cannot see at all, the ones in no group.
 *
 * A group whose members cannot be read does NOT reduce somebody's group
 * count silently: it lands in `unreadableGroups` and the screen says
 * the list is incomplete.
 */
export async function listPeople(params: {
  role?: string;
  limit?: number;
  offset?: number;
}): Promise<PeopleListOut> {
  const [page, groupList] = await Promise.all([
    adminUsersApi.list({
      role: params.role,
      limit: params.limit,
      offset: params.offset,
    }),
    groupsApi.list(),
  ]);

  const byUser = new Map<string, PersonRow["groups"]>();
  const scopeByUser = new Map<string, string>();
  const scopeIdsByUser = new Map<string, string[]>();
  const unreadable: PeopleListOut["unreadableGroups"] = [];

  const fetched = await Promise.all(
    groupList.groups.map(async (g) => {
      try {
        return { group: g, members: await groupsApi.allMembers(g.id) };
      } catch (err) {
        unreadable.push({
          group_id: g.id,
          group_name: g.name,
          error: err instanceof Error ? err.message : String(err),
        });
        return null;
      }
    }),
  );

  for (const entry of fetched) {
    if (entry === null) continue;
    for (const member of entry.members) {
      const list = byUser.get(member.user_id) ?? [];
      list.push({
        id: entry.group.id,
        name: entry.group.name,
        scope_id: entry.group.scope_id,
        scope_name: entry.group.scope_name,
      });
      byUser.set(member.user_id, list);
      scopeByUser.set(member.user_id, member.effective_scope_id);
      scopeIdsByUser.set(member.user_id, member.group_scope_ids);
    }
  }

  return {
    people: page.items.map((user) => ({
      user,
      groups: byUser.get(user.id) ?? [],
      effective_scope_id: scopeByUser.get(user.id) ?? null,
      group_scope_ids: scopeIdsByUser.get(user.id) ?? [],
    })),
    total: page.total,
    limit: page.limit,
    offset: page.offset,
    unreadableGroups: unreadable,
    loginsRecordStartsAt: page.logins_record_starts_at,
  };
}

// ── Audit ────────────────────────────────────────────────────────────

/**
 * One row of `access_audit`.
 *
 * `actor_email` is NOT NULL beside a nullable `actor_user_id` precisely
 * so the line survives the person (migration 054), which is why the
 * e-mail is the column and the id is detail. `before_json` / `after_json`
 * are nullable ON PURPOSE — a create has no before, a delete has no
 * after — so the detail view says "no existía" and never renders an
 * empty object, which would be an invented value.
 */
export interface AccessAuditOut {
  id: string;
  created_at: string;
  actor_user_id: string | null;
  actor_email: string;
  actor_kind: string;
  action: string;
  target_kind: string;
  target_id: string;
  before_json: Record<string, unknown> | null;
  after_json: Record<string, unknown> | null;
}

export interface AccessAuditListOut {
  entries: AccessAuditOut[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The read window onto `access_audit`.
 *
 * `builder/api/routes/access_audit.py` answers this prefix, `main.py`
 * includes it, and the guard is `RequireManageUsers` — reads included,
 * because the trail carries states the access map no longer holds and
 * cannot be gated more loosely than the map itself. The route is
 * GET-only by construction: `service_access_audit.record` remains the
 * single WRITER of the table and is called inside the transaction of the
 * mutation it records, so nothing here writes and nothing here may.
 *
 * WHY THE OLD WARNING IS KEPT RATHER THAN DELETED
 * This block used to open **NOT SHIPPED BY THE BACKEND YET**, and it was
 * accurate: the table had a writer and no reader, and every call here
 * answered 404 while `AccessAuditPage` rendered against it. It shipped
 * that way on a stated argument — the shape below is the table's columns
 * (`apps/hp-portal/alembic/versions/054_scope_columns_and_access_audit.py`),
 * so writing the client first pinned what the handler would have to
 * answer instead of letting it invent a second shape. That half worked:
 * the handler answers this envelope. The half that did not is worth
 * carrying forward — for the interval in between, a screen whose own
 * help text says *"una acción que no está acá no ocurrió"* was reachable
 * with no trail behind it. A client may be written ahead of its route;
 * the SCREEN may not be reachable ahead of it.
 *
 * Filters are server-side only. `limit` is echoed back so the screen can
 * report an out-of-range value instead of quietly substituting a
 * default — and out of range is a 422 from the route, never a clamp.
 */
export const accessAuditApi = {
  list: (opts?: {
    action?: string;
    actorEmail?: string;
    targetKind?: string;
    targetId?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<AccessAuditListOut>("/api/builder/admin/access-audit", {
      query: {
        action: opts?.action,
        actor_email: opts?.actorEmail,
        target_kind: opts?.targetKind,
        target_id: opts?.targetId,
        limit: opts?.limit,
        offset: opts?.offset,
      },
    }),
};
